import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography
} from "@outschool/backpack";
import { Search } from "@outschool/business-rules";
import { SearchFilters as SearchFiltersType } from "@outschool/gql-backend-generated";
import { I18nLocale, useLocale, useTranslation } from "@outschool/localization";
import * as Time from "@outschool/time";
import {
  FundingProgramButton,
  useShowCredits,
  useShowFundingProgramButton
} from "@outschool/ui-components-website";
import React from "react";

import EnglishProficiencyFilter from "../../EnglishProficiencyFilter";
import { Filter, FilterList } from "../Filter";
import FilterActionButtons from "../FilterActionButtons";
import { UpdatedFilterProps } from "../FilterMenuToggle";
import AgeFilter from "./AgeFilter";
import ClassSizeFilter from "./ClassSizeFilter";
import { CreditFilter } from "./CreditFilter";
import DateFilter from "./DateFilter";
import { GradeLevelFilter } from "./LevelFilter";
import { ContextualPriceFilter } from "./PriceFilter";
import ThemeFilter from "./ThemeFilter";
import TimeFilter from "./TimeFilter";
import UncommonFilters from "./UncommonFilters";

type ListingFiltersProps = {
  searchFilters: SearchFiltersType;
  onApply: (props: UpdatedFilterProps) => void;
  searchUid?: string;
};

type ListingFilterParams = {
  pricePerMeetingMin?: number;
  pricePerMeetingMax?: number;
  theme?: string;
  age?: string | null;
  gradeLevel?: string | null;
  startBefore?: string;
  startAfter?: string;
  dow?: any;
  startAfterTime?: number;
  endByTime?: number;
  capacityMin?: number;
  capacityMax?: number;
  includeInProgressFixedLengthLiveFormat?: boolean;
  durationWeeksMin?: number;
  durationWeeksMax?: number;
  weeklyMeetingsMin?: number;
  weeklyMeetingsMax?: number;
};

export type ListingChildFilterProps = {
  showTopBorder?: boolean;
  searchFilters: SearchFiltersType;
  onChange?: (newValue: ListingFilterParams) => void;
  onChangeWithParam?: (filterParam: string, newValue: string[] | null) => void;

  isCuratedCategoryFilter?: boolean;
  hideFormat?: boolean;
  hideClassSize?: boolean;
  hideClassType?: boolean;
  hideChatGroupInsideClassType?: boolean;
  hideLanguage?: boolean;
  hideEvaluation?: boolean;
  hideCurriculums?: boolean;
  hideStandards?: boolean;
  withAccordions?: boolean;
  hideTitle?: boolean;
};

enum CommonFilters {
  Age = "age",
  Subject = "subject",
  Schedule = "schedule",
  Price = "price",
  Credit = "credit",
  ClassSize = "class_size",
  EngilishProficiency = "engilish_proficiency",
  UsGradeLevel = "us_grade_level"
}

type FilterConfig = {
  title: string;
  isFilterActive: boolean;
  numberOfOptionsApplied: number;
  resetFilters(): void;
  isShowing: boolean;
  component: React.ReactNode;
};

export default function ListingFilters({
  searchFilters,
  onApply,
  searchUid
}: ListingFiltersProps) {
  const { t } = useTranslation("client\\components\\search\\mobile\\filters");
  const hasSubscription = useShowCredits();
  const activeSearchToggle = searchFilters
    ? Search.activeSearchToggleFromFilters(searchFilters)
    : Search.SearchToggleType.ALL;

  const locale = useLocale();
  const showEnglishProficiencyFilter = [
    I18nLocale.Ja,
    I18nLocale.Ko,
    I18nLocale.ZhTw
  ].includes(locale);

  const [tempSearchFilters, setTempSearchFilters] = React.useState(
    searchFilters || {}
  );
  const updateSearchFilters = (newFilters: SearchFiltersType) => {
    setTempSearchFilters(existing => {
      return {
        ...existing,
        ...newFilters
      };
    });
  };

  const {
    showChatGroupInsideClassType,
    showClassSizeFilter,
    showClassTypeFilterInUncommonFilters,
    showCurriculums,
    showEvaluation,
    showStandards
  } = Search.useContextualFilters({
    activeSearchToggle,
    isMobile: true
  });

  const showFundingProgramButton =
    useShowFundingProgramButton(tempSearchFilters);

  const getActiveDateFilters = (filters: $TSFixMe) => {
    const defaults = {
      startAfter: Search.RelativeDateFilter.AllUpcoming,
      startBefore: null,
      includeInProgressFixedLengthLiveFormat: true,
      dow: null,
      startAfterTime: Time.DAY_START_HOUR,
      endByTime: Time.DAY_END_HOUR,
      durationWeeksMin: null,
      durationWeeksMax: null,
      weeklyMeetingsMax: 7,
      weeklyMeetingsMin: 0
    };
    const activeFilters: string[] = [];

    for (const key in filters) {
      if (filters.hasOwnProperty(key) && defaults.hasOwnProperty(key)) {
        // @ts-ignore
        if (filters[key] !== defaults[key]) {
          activeFilters.push(key);
        }
      }
    }

    return activeFilters;
  };

  const commonFilters: Record<CommonFilters, FilterConfig> = {
    [CommonFilters.Age]: {
      title: t("Learner age"),
      isFilterActive: !!tempSearchFilters.age?.split(",").filter(age => !!age)
        .length,
      numberOfOptionsApplied:
        tempSearchFilters.age?.split(",").filter(age => !!age).length ?? 0,
      resetFilters: () => {
        updateSearchFilters({
          age: null
        });
      },
      isShowing: true,
      component: (
        <>
          <AgeFilter
            searchFilters={tempSearchFilters}
            onChange={newValue => {
              updateSearchFilters(newValue);
            }}
          />
        </>
      )
    },
    [CommonFilters.Subject]: {
      title: t("Subject"),
      isFilterActive: !!tempSearchFilters.theme?.split(",").filter(age => !!age)
        .length,
      numberOfOptionsApplied:
        tempSearchFilters.theme?.split(",").filter(age => !!age).length ?? 0,
      resetFilters: () => {
        updateSearchFilters({
          theme: null
        });
      },
      isShowing: true,
      component: (
        <ThemeFilter
          searchFilters={tempSearchFilters}
          onChange={newValue => {
            updateSearchFilters(newValue);
          }}
          selectedSearchToggle={activeSearchToggle}
        />
      )
    },
    [CommonFilters.Schedule]: {
      title: t("Schedule"),
      isFilterActive: getActiveDateFilters(tempSearchFilters).length > 0,

      numberOfOptionsApplied: getActiveDateFilters(tempSearchFilters).length,
      resetFilters: () => {
        updateSearchFilters({
          startAfter: Search.RelativeDateFilter.AllUpcoming,
          startBefore: null,
          includeInProgressFixedLengthLiveFormat: true,
          dow: null,
          startAfterTime: Time.DAY_START_HOUR,
          endByTime: Time.DAY_END_HOUR,
          durationWeeksMin: null,
          durationWeeksMax: null,
          weeklyMeetingsMax: 7,
          weeklyMeetingsMin: 0
        });
      },
      isShowing: true,
      component: (
        <>
          <DateFilter
            showTopBorder={showFundingProgramButton}
            searchFilters={tempSearchFilters}
            onChange={newValue => {
              updateSearchFilters({
                startAfter: null,
                startBefore: null,
                ...newValue
              });
            }}
          />
          <TimeFilter
            searchFilters={tempSearchFilters}
            onChange={newValue => {
              updateSearchFilters(newValue);
            }}
          />
        </>
      )
    },
    [CommonFilters.Price]: {
      title: t("Price"),
      isFilterActive:
        !!tempSearchFilters.pricePerMeetingMax ||
        !!tempSearchFilters.pricePerMeetingMin ||
        !!tempSearchFilters.pricePerCourseMax ||
        !!tempSearchFilters.pricePerCourseMin,
      numberOfOptionsApplied: 1,
      resetFilters: () => {
        updateSearchFilters({
          pricePerMeetingMin: null,
          pricePerMeetingMax: null,
          pricePerCourseMax: null,
          pricePerCourseMin: null
        });
      },
      isShowing: true,
      component: (
        <ContextualPriceFilter
          searchFilters={tempSearchFilters}
          onChange={newValue => {
            updateSearchFilters({
              pricePerMeetingMin: null,
              pricePerMeetingMax: null,
              pricePerCourseMax: null,
              pricePerCourseMin: null,
              ...newValue
            });
          }}
          selectedSearchToggle={activeSearchToggle}
        />
      )
    },
    [CommonFilters.Credit]: {
      title: t("Cost"),
      isFilterActive:
        !!tempSearchFilters.priceCreditsPerMeetingMax ||
        !!tempSearchFilters.priceCreditsPerMeetingMin ||
        !!tempSearchFilters.priceCreditsPerCourseMax ||
        !!tempSearchFilters.priceCreditsPerCourseMin,
      numberOfOptionsApplied: 1,
      resetFilters: () => {
        updateSearchFilters({
          priceCreditsPerMeetingMin: null,
          priceCreditsPerMeetingMax: null,
          priceCreditsPerCourseMax: null,
          priceCreditsPerCourseMin: null
        });
      },
      isShowing: true,
      component: (
        <CreditFilter
          searchFilters={tempSearchFilters}
          onChange={newValue => {
            updateSearchFilters({
              pricePerMeetingMin: null,
              pricePerMeetingMax: null,
              pricePerCourseMax: null,
              pricePerCourseMin: null,
              priceCreditsPerMeetingMin: null,
              priceCreditsPerMeetingMax: null,
              priceCreditsPerCourseMax: null,
              priceCreditsPerCourseMin: null,
              ...newValue
            });
          }}
          isCourse={activeSearchToggle === Search.SearchToggleType.COURSES}
        />
      )
    },
    [CommonFilters.ClassSize]: {
      title: t("Class size"),
      isFilterActive:
        !!tempSearchFilters.capacityMax || !!tempSearchFilters.capacityMin,
      numberOfOptionsApplied: 1,
      resetFilters: () => {
        updateSearchFilters({
          capacityMin: null,
          capacityMax: null
        });
      },
      isShowing: showClassSizeFilter,
      component: (
        <ClassSizeFilter
          searchFilters={tempSearchFilters}
          onChange={newValue => {
            const updatedSearchFilters = {
              ...tempSearchFilters,
              ...newValue
            };

            // Tab might change due to class size filter, so we need to reset some filters.
            const updatedFilterValue = Search.newFiltersFromSearchToggleChange(
              activeSearchToggle,
              updatedSearchFilters
            );

            updateSearchFilters(updatedFilterValue);
          }}
        />
      )
    },
    [CommonFilters.EngilishProficiency]: {
      title: t("English Proficiency"),
      isFilterActive: !!tempSearchFilters.englishProficiencyLevel,
      numberOfOptionsApplied: 1,
      resetFilters: () => {
        updateSearchFilters({
          englishProficiencyLevel: null
        });
      },
      isShowing: showEnglishProficiencyFilter,
      component: (
        <Filter showTopBorder={false}>
          <EnglishProficiencyFilter
            onChange={newValue => {
              updateSearchFilters({
                englishProficiencyLevel: newValue
              });
            }}
            currentFilterProficiencyLevel={
              tempSearchFilters?.englishProficiencyLevel
            }
          />
        </Filter>
      )
    },
    [CommonFilters.UsGradeLevel]: {
      title: t("US grade level"),
      isFilterActive: !!tempSearchFilters.gradeLevel
        ?.split(",")
        .filter(gradeLevel => !!gradeLevel).length,
      numberOfOptionsApplied:
        tempSearchFilters.gradeLevel
          ?.split(",")
          .filter(gradeLevel => !!gradeLevel).length ?? 0,
      resetFilters: () => {
        updateSearchFilters({
          gradeLevel: null
        });
      },
      isShowing: true,
      component: (
        <GradeLevelFilter
          hideTitle
          searchFilters={tempSearchFilters}
          onChange={newValue => {
            updateSearchFilters(newValue);
          }}
        />
      )
    }
  };

  const getCommonFiltersWithAccordion = (commonFilterList: CommonFilters[]) => {
    const filtersToShow = commonFilterList.map(filter => commonFilters[filter]);
    return (
      <>
        {filtersToShow.map(filter => {
          return (
            filter.isShowing && (
              <Accordion key={filter.title}>
                <AccordionSummary>
                  <>
                    <Typography>{filter.title}</Typography>
                    {filter.isFilterActive && (
                      <Typography
                        sx={(theme: $TSFixMe) => ({
                          color: theme.palette.primary.main,
                          marginLeft: 5
                        })}
                        onClick={(event: $TSFixMe) => {
                          event.stopPropagation();
                          filter.resetFilters();
                        }}
                      >
                        {t("Clear ({{count}})", {
                          count: filter.numberOfOptionsApplied
                        })}
                      </Typography>
                    )}
                  </>
                </AccordionSummary>
                <AccordionDetails>{filter.component}</AccordionDetails>
              </Accordion>
            )
          );
        })}
      </>
    );
  };

  return (
    <FilterList>
      {getCommonFiltersWithAccordion([
        CommonFilters.Age,
        CommonFilters.Subject
      ])}

      {showFundingProgramButton && (
        <Filter showTopBorder={true}>
          <Box
            sx={{
              padding: "12px 24px 24px 0px"
            }}
          >
            <FundingProgramButton
              searchUid={searchUid}
              searchFilters={tempSearchFilters}
              updateSearchFilters={updateSearchFilters}
            />
          </Box>
        </Filter>
      )}
      {getCommonFiltersWithAccordion([
        CommonFilters.Schedule,
        hasSubscription ? CommonFilters.Credit : CommonFilters.Price
      ])}

      <UncommonFilters
        withAccordions
        hideFormat
        hideCurriculums={!showCurriculums}
        hideStandards={!showStandards}
        hideClassType={!showClassTypeFilterInUncommonFilters}
        hideChatGroupInsideClassType={!showChatGroupInsideClassType}
        hideEvaluation={!showEvaluation}
        searchFilters={tempSearchFilters}
        onChangeWithParam={(filterParam: string, newValue: string[] | null) => {
          let updateFilter = {};
          // @ts-ignore
          updateFilter[filterParam] = newValue?.join(",");
          updateSearchFilters({
            // @ts-ignore
            ...tempSearchFilters["Uncommon"],
            ...updateFilter
          });
        }}
      />
      {getCommonFiltersWithAccordion([
        CommonFilters.ClassSize,
        CommonFilters.EngilishProficiency,
        CommonFilters.UsGradeLevel
      ])}

      <FilterActionButtons
        onApply={() => {
          onApply({
            type: "additional",
            filter: tempSearchFilters
          });
        }}
        resetFilters={() => {
          updateSearchFilters({
            age: null,
            theme: null,
            startAfter: Search.RelativeDateFilter.AllUpcoming,
            startBefore: null,
            includeInProgressFixedLengthLiveFormat: true,
            dow: null,
            startAfterTime: Time.DAY_START_HOUR,
            endByTime: Time.DAY_END_HOUR,
            weeklyMeetingsMin: 0,
            weeklyMeetingsMax: 7,
            durationWeeksMin: null,
            durationWeeksMax: null,
            pricePerMeetingMin: null,
            pricePerMeetingMax: null,
            pricePerCourseMax: null,
            pricePerCourseMin: null,
            gradeLevel: null,
            capacityMin: null,
            capacityMax: null,
            englishProficiencyLevel: null,
            format: null,
            curriculums: null,
            standards: null,
            languageOfInstruction: null,
            hasHomework: null,
            hasGrades: null,
            hasAssessment: null,
            delivery: null
          });
        }}
      />
    </FilterList>
  );
}
